import type { MouseEvent } from "react";
import { DimensionType, DetectionStatus } from "../../../hooks/useAutoMonitor.utils";

export interface StatisticCardConfig {
  key: string;
  title: string;
  value: number;
  suffix?: string;
  clickable?: boolean;
  onClick?: (item: unknown, event: MouseEvent) => void;
  valueStyle?: { color?: string };
}

export interface StatisticCardValues {
  priceAdjustmentStores?: number;
  priceAnomaly?: number;
  fullyBooked?: number;
  customerSourceAnomaly?: number;
  salesStoppedAnomaly?: number;
  reviewAnomaly?: number;
  majorEventAreas?: number;
  occupancyRateAnomaly?: number;
}

export const createStatisticData = (
  onCardClick?: (item: any, event: MouseEvent) => void,
  values?: StatisticCardValues
): StatisticCardConfig[] => {
  const cards: StatisticCardConfig[] = [
    { key: "price-adjustment-stores", title: "待调价门店", value: values?.priceAdjustmentStores ?? 0, suffix: "家", clickable: true, onClick: onCardClick, valueStyle: { color: "#1890ff" } },
    { key: "price-anomaly", title: "价格异常", value: values?.priceAnomaly ?? 0, suffix: "家", clickable: true, onClick: onCardClick, valueStyle: { color: "#ff4d4f" } },
    { key: "fully-booked", title: "满房", value: values?.fullyBooked ?? 0, suffix: "家", clickable: true, onClick: onCardClick, valueStyle: { color: "#52c41a" } },
    { key: "customer-source-anomaly", title: "客源异常", value: values?.customerSourceAnomaly ?? 0, suffix: "家", clickable: true, onClick: onCardClick, valueStyle: { color: "#faad14" } },
    { key: "sales-stopped-anomaly", title: "停售异常", value: values?.salesStoppedAnomaly ?? 0, suffix: "家", clickable: true, onClick: onCardClick, valueStyle: { color: "#722ed1" } },
    { key: "review-anomaly", title: "评价异常", value: values?.reviewAnomaly ?? 0, suffix: "家", clickable: true, onClick: onCardClick, valueStyle: { color: "#eb2f96" } },
    { key: "major-event-areas", title: "大事件地区", value: values?.majorEventAreas ?? 0, suffix: "家", clickable: true, onClick: onCardClick, valueStyle: { color: "#13c2c2" } },
    { key: "occupancy-rate-anomaly", title: "出租率异常", value: values?.occupancyRateAnomaly ?? 0, suffix: "家", clickable: true, onClick: onCardClick, valueStyle: { color: "#fa8c16" } },
  ];
  return cards;
};

export function calculateStatisticValues(
  dimensionResults: Array<{ dimension: DimensionType; status: DetectionStatus; storeId: number }>
): StatisticCardValues {
  const values: StatisticCardValues = {};
  const dimensionMap = new Map<DimensionType, Set<number>>();

  dimensionResults.forEach((result) => {
    if (result.status !== DetectionStatus.NORMAL) {
      if (!dimensionMap.has(result.dimension)) {
        dimensionMap.set(result.dimension, new Set());
      }
      dimensionMap.get(result.dimension)!.add(result.storeId);
    }
  });

  values.priceAnomaly = (dimensionMap.get(DimensionType.PRICE) || new Set()).size;
  const priceAdjustmentStoreIds = new Set(
    dimensionResults
      .filter(r => r.dimension === DimensionType.PRICE && r.status === DetectionStatus.WARNING)
      .map(r => r.storeId)
  );
  values.priceAdjustmentStores = priceAdjustmentStoreIds.size;

  values.occupancyRateAnomaly = (dimensionMap.get(DimensionType.OCCUPANCY) || new Set()).size;
  const fullyBookedStoreIds = new Set(
    dimensionResults
      .filter(r => r.dimension === DimensionType.OCCUPANCY && r.status === DetectionStatus.WARNING)
      .map(r => r.storeId)
  );
  values.fullyBooked = fullyBookedStoreIds.size;

  values.customerSourceAnomaly = (dimensionMap.get(DimensionType.CUSTOMER_SOURCE) || new Set()).size;
  values.salesStoppedAnomaly = (dimensionMap.get(DimensionType.ROOM_STATUS) || new Set()).size;
  const reviewAnomalyStoreIds = new Set(
    dimensionResults
      .filter(r => r.dimension === DimensionType.OVERALL_HEALTH && r.status !== DetectionStatus.NORMAL)
      .map(r => r.storeId)
  );
  values.reviewAnomaly = reviewAnomalyStoreIds.size;
  values.majorEventAreas = (dimensionMap.get(DimensionType.TIME) || new Set()).size;

  return values;
}
